import { getStoreSearchDisplayPrice } from "../utils/storeSearchSort";
import type { StoreProductResult } from "../services/ProductSearchService";
import type { HomeDepotStoreProductResult } from "../services/ProductSearchService";

export const formatPrice = (price: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(price);
};

export const isTargetLimitedStock = (retailer: string, status?: string) =>
  (retailer === "target" || retailer === "target_online") &&
  status?.toUpperCase() === "LIMITED_STOCK";

export const isNearbyStoreInStock = (item: { status?: string; inventory?: number; backroom?: number; error?: string }) => {
  if (item.error || item.status?.toUpperCase() === "OUT_OF_STOCK") return false;
  return item.status?.toUpperCase() === "IN_STOCK" ||
    item.status?.toUpperCase() === "LIMITED_STOCK" ||
    (item.inventory ?? 0) > 0 || (item.backroom ?? 0) > 0;
};

export const getNearbyMsrp = (
  item: { msrp?: number; price?: number; originalPrice?: number },
  fallbackMsrp?: number
) => [item.msrp, item.originalPrice, item.price, fallbackMsrp].find(
  (price): price is number => typeof price === "number" && Number.isFinite(price) && price > 0
);

export const getNearbyDiscountPercentage = (storePrice: number, msrp?: number): number | null => {
  if (!Number.isFinite(storePrice) || storePrice <= 0 || msrp == null || !Number.isFinite(msrp) || msrp <= 0 || storePrice >= msrp) return null;
  return Math.round(((msrp - storePrice) / msrp) * 100);
};

export const isBestNearbyDeal = (input: {
  retailer: string;
  status?: string;
  storePrice: number;
  msrp: number | undefined;
  cheapestPrice: number;
  hasTargetClearance: boolean;
}) =>
  getNearbyDiscountPercentage(input.storePrice, input.msrp) !== null &&
  (isTargetLimitedStock(input.retailer, input.status) ||
    (!input.hasTargetClearance && input.storePrice === input.cheapestPrice));

export const getCheapestPrice = (input: {
  isHomeDepot: boolean;
  searchResults?: StoreProductResult[];
  homeDepotSearchResults?: HomeDepotStoreProductResult[];
}) => {
  const { isHomeDepot, searchResults, homeDepotSearchResults } = input;
  if (isHomeDepot) {
    if (searchResults && searchResults.length > 0) {
      const inStockResults = searchResults.filter(
        (result) => isNearbyStoreInStock(result) && Number.isFinite(getStoreSearchDisplayPrice(result)) && getStoreSearchDisplayPrice(result) > 0
      );
      if (inStockResults.length > 0) {
        return Math.min(...inStockResults.map(getStoreSearchDisplayPrice));
      }
    }
    if (homeDepotSearchResults) {
      const inStockResults = homeDepotSearchResults.filter(
        (result) => isNearbyStoreInStock(result) && Number.isFinite(getStoreSearchDisplayPrice(result)) && getStoreSearchDisplayPrice(result) > 0
      );
      if (inStockResults.length > 0) {
        return Math.min(...inStockResults.map(getStoreSearchDisplayPrice));
      }
    }
    return Infinity;
  }

  if (!isHomeDepot && searchResults) {
    const inStockResults = searchResults.filter(
      (result) => isNearbyStoreInStock(result) && Number.isFinite(getStoreSearchDisplayPrice(result)) && getStoreSearchDisplayPrice(result) > 0
    );
    if (inStockResults.length === 0) return Infinity;
    return Math.min(...inStockResults.map(getStoreSearchDisplayPrice));
  }

  return Infinity;
};

const resolveWalmartSkuBarcode = (
  sku?: string,
  barcode?: string,
  retailer?: string
): { sku?: string; barcode?: string } => {
  if (retailer !== "walmart" && retailer !== "walmart_online") return { sku, barcode };
  if (!sku || !barcode || sku === barcode) return { sku, barcode };
  return sku.length > barcode.length ? { sku: barcode, barcode: sku } : { sku, barcode };
};

export const getProductInfo = (input: {
  isHomeDepot: boolean;
  searchResults?: StoreProductResult[];
  homeDepotSearchResults?: HomeDepotStoreProductResult[];
  retailer?: string;
}) => {
  const { isHomeDepot, searchResults, homeDepotSearchResults, retailer } = input;
  let productInfo: { name: string; image?: string; sku?: string; barcode?: string; msrp?: number } | null = null;

  if (!isHomeDepot && searchResults && searchResults.length > 0) {
    const firstResult = searchResults.find(
      isNearbyStoreInStock
    ) || searchResults.find((result) => !result.error);
    if (firstResult) {
      const resolved = resolveWalmartSkuBarcode(firstResult.sku, firstResult.barcode || undefined, retailer);
      productInfo = {
        name: firstResult.name,
        image: firstResult.image,
        sku: resolved.sku,
        barcode: resolved.barcode,
        msrp: getNearbyMsrp(firstResult),
      };
    }
  } else if (isHomeDepot) {
    if (searchResults && searchResults.length > 0) {
      const firstResult = searchResults.find(
        isNearbyStoreInStock
      ) || searchResults.find((result) => !result.error);
      if (firstResult) {
        productInfo = {
          name: firstResult.name,
          image: firstResult.image,
          sku: firstResult.sku,
          barcode: firstResult.barcode || undefined,
          msrp: getNearbyMsrp(firstResult),
        };
      }
    } else if (homeDepotSearchResults && homeDepotSearchResults.length > 0) {
      const firstResult = homeDepotSearchResults.find(
        isNearbyStoreInStock
      ) || homeDepotSearchResults.find((result) => !result.error);
      if (firstResult) {
        productInfo = {
          name: firstResult.name,
          image: firstResult.image,
          sku: firstResult.sku,
          barcode: firstResult.barcode || firstResult.upc || undefined,
          msrp: getNearbyMsrp(firstResult),
        };
      }
    }
  }

  return productInfo;
};

export const getWalmartBarcode = (input: {
  retailer: string;
  searchResults?: StoreProductResult[];
}) => {
  if (input.retailer !== "walmart") return null;
  if (input.searchResults && input.searchResults.length > 0) {
    const barcodeResult = input.searchResults.find(
      (r) => !r.error && r.barcode
    );
    return barcodeResult?.barcode || null;
  }
  return null;
};

export const getFirstStoreSearchError = (
  results?: Array<StoreProductResult | HomeDepotStoreProductResult>
) => {
  const message = results?.find((result) => result.error)?.error?.trim();
  return message || null;
};
